import { useEffect, useMemo, useState } from 'react';
import {
  RefreshCw,
  Trophy,
  Medal,
  TrendingUp,
  MapPin,
  ChevronRight,
  Star,
  Bell,
  LogOut,
  Bug,
} from 'lucide-react';
import { getEloLevel } from '../../../engine';
import { memberSingleMatches } from '../memberData';
import { nextLevelInfo } from '../eloData';
import type { PushStatus } from '../../../state/usePushRegistration';
import { PasskeySection } from '../../account/PasskeySection';
import { SessionsSection } from '../../account/SessionsSection';
import { MyProfileEditor } from '../../account/MyProfileEditor';
import { CloseAccountSection } from '../../account/CloseAccountSection';
import { DataExportSection } from '../../account/DataExportSection';
import { EraseAccountSection } from '../../account/EraseAccountSection';
import { BigButton, Card, LevelChip, SectionTitle, initials } from '../mobileUi';
import { BugReportModal } from '../../bugs/BugReportModal';
import { loadDeploymentConfig } from '../../../lib/api/deploymentConfig';
import { playerSurfaceActive } from '../../../lib/api/customerContext';
import { DEFAULT_LOCALE, translate, useT, type LocaleContextValue } from '../../../i18n';

export interface MobileProfileProps {
  member: any;
  singleMatches: any[];
  tournamentCount: number;
  /** Name of the favourite padel centre (falls back to the home club). */
  favoriteCenter?: string | null;
  /**
   * Open the «who are you» sheet (the shared `demo_user_v1` pick).
   *
   * NULL FOR A PROVEN IDENTITY (#791). A member session or an account session
   * on the player surface already names this person, so there is nothing to
   * switch to — and offering the choice would show them a list of other
   * players to be, with the answer silently discarded by the guard in
   * `useCurrentMember`. The section then states who they are instead.
   */
  onSwitchClick: (() => void) | null;
  /** Open the padel-centre list. */
  onOpenCenters: () => void;
  /**
   * Open the 📍 club selector (owner, 2026-10-16).
   *
   * Null wherever there is no context to choose: a member session's club is
   * the session's own, and the portal's preview shows the portal's club.
   */
  onChooseClub?: (() => void) | null;
  /** The active club's name, shown on the selector row; null for none. */
  clubName?: string | null;
  /**
   * Sign out of StarPoint. Null on the portal's preview, which has its own
   * header and whose session is not the player's.
   *
   * THE APP NEEDS ITS OWN. The placeholder home that carried a «Logg ut» is
   * gone, and the native shell has no header — without this a player in the
   * app had no way to sign out at all.
   */
  onSignOut?: (() => void) | null;
  /**
   * Push notifications (usePushRegistration). Only passed inside the native
   * shell — in the browser preview the section is not rendered at all.
   */
  push?: { status: PushStatus; error?: string | null; enable: () => void };
  /**
   * Show the ACCOUNT's passkeys — list, add, remove — under «Logget inn som».
   *
   * The portal's own `PasskeySection`, reused rather than copied. Only for an
   * account on the player surface: a legacy member session has no account to
   * hold a credential, and the portal's preview has the portal's account
   * settings for it. False (the default) renders nothing.
   */
  accountPasskeys?: boolean;
  /**
   * Whether this deployment lets every signed-in user file a bug report
   * (owner decision 2026-10-22). Defaults to asking `/config`; injectable for
   * tests. Closed hides the entry: offering a form the server refuses is the
   * worse failure.
   */
  loadBugReportsOpen?: () => Promise<boolean>;
}

const bugReportsOpenFromConfig = async () => (await loadDeploymentConfig()).bugReports.open;

// «Profil» — who you are, your level and ELO, headline stats, your padel
// centres, and the demo profile switch (the same demo_user_v1 pick
// ActivitiesView uses, so both surfaces agree on who «you» are).
export function MobileProfile({
  member,
  singleMatches,
  tournamentCount,
  favoriteCenter,
  onSwitchClick,
  onOpenCenters,
  onChooseClub = null,
  clubName = null,
  onSignOut = null,
  push,
  accountPasskeys = false,
  loadBugReportsOpen = bugReportsOpenFromConfig,
}: MobileProfileProps) {
  const t = useT();
  // «MELD FEIL ELLER FORSLAG» (owner decision 2026-10-22). The floating bug
  // button is hidden in the member app — it would cover the tab bar — so a
  // player reports from here. Shown only where the deployment opens filing.
  const [reportOpen, setReportOpen] = useState(false);
  const [showReport, setShowReport] = useState(false);
  useEffect(() => {
    let live = true;
    void loadBugReportsOpen()
      .then((open) => {
        if (live) setReportOpen(open === true);
      })
      .catch(() => {
        if (live) setReportOpen(false);
      });
    return () => {
      live = false;
    };
  }, [loadBugReportsOpen]);
  const elo = Math.round(member?.elo || 0);
  const lvl = getEloLevel(elo);
  const nextLvl = nextLevelInfo(elo);

  const played = useMemo(
    () => memberSingleMatches(singleMatches, member?.id),
    [singleMatches, member],
  );
  const wins = played.filter((p) => p.won === true).length;
  const losses = played.filter((p) => p.won === false).length;
  const winPct = played.length ? Math.round((wins / played.length) * 100) : null;
  const eloTrend = played.slice(0, 5).reduce((s, p) => s + (p.eloChange || 0), 0);

  if (!member) {
    return (
      <div className="px-4 pt-6 text-center text-ink-3 text-[13px]">
        {t('mobile.profile.noPlayers')}
      </div>
    );
  }

  return (
    // ── ON A WIDE SCREEN: TWO COLUMNS, SAME SECTIONS, SAME ORDER ───────
    // (desktop brief, step 2). Who you are as a player on the left — identity,
    // the numbers, the level and its traits; your centres and your account on
    // the right. PLACED by the grid, never reordered: the phone and a screen
    // reader read exactly what they read before.
    <div className="px-4 pb-4 space-y-4 @wide:space-y-0 @wide:grid @wide:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] @wide:[grid-template-rows:auto_auto_auto_auto_1fr] @wide:gap-x-8 @wide:gap-y-5 @wide:items-start">
      {/* Identity */}
      <div className="flex items-center gap-4 pt-3 @wide:col-start-1 @wide:row-start-1">
        <div
          className="flex items-center justify-center rounded-full font-display font-semibold flex-shrink-0"
          style={{ width: 68, height: 68, background: lvl.color, color: lvl.text, fontSize: 24 }}
        >
          {initials(member.name)}
        </div>
        <div className="min-w-0">
          <h1
            className="font-display font-semibold text-ink leading-tight truncate"
            style={{ fontSize: 24 }}
          >
            {member.name}
          </h1>
          <div className="mt-1">
            <LevelChip elo={elo} />
          </div>
        </div>
      </div>

      {/* Headline numbers */}
      <div className="grid grid-cols-3 gap-2 @wide:col-start-1 @wide:row-start-2">
        <Stat label={t('mobile.profile.statElo')} value={elo} />
        <Stat
          label={t('mobile.profile.statWins')}
          value={played.length ? `${wins}–${losses}` : '–'}
          sub={winPct != null ? `${winPct}%` : undefined}
        />
        <Stat
          label={t('mobile.profile.statTournaments')}
          value={tournamentCount || member.tournamentCount || member.tournamentsPlayed || 0}
        />
      </div>

      {/* Level detail */}
      <Card className="@wide:col-start-1 @wide:row-start-3">
        <div className="flex items-start gap-3">
          <TrendingUp size={18} className="text-ink-3 mt-0.5 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-[14px] font-semibold text-ink">
              {t('mobile.profile.levelLine', { level: String(lvl.level), name: lvl.name })}
            </div>
            <div className="text-[12px] text-ink-2 mt-0.5">{lvl.description}</div>
            {nextLvl.next && (
              <div className="text-[12px] font-semibold text-ink mt-1">
                {t('mobile.elo.pointsToNext', {
                  points: nextLvl.pointsToNext,
                  level: `N${nextLvl.next.level}`,
                  name: nextLvl.next.name,
                })}
              </div>
            )}
            <div className="text-[11px] text-ink-3 mt-1.5 font-mono">
              {lvl.min}–{lvl.max === 9999 ? '∞' : lvl.max} ELO
              {played.length > 0 && (
                <span className="ml-2" style={{ color: eloTrend >= 0 ? '#3f6212' : '#be123c' }}>
                  {t('mobile.profile.last5')} {eloTrend >= 0 ? '+' : ''}
                  {Math.round(eloTrend)}
                </span>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Padel centres */}
      <div className="@wide:col-start-2 @wide:row-start-1">
        <SectionTitle>{t('mobile.common.centres')}</SectionTitle>
        <Card onClick={onOpenCenters}>
          <div className="flex items-center gap-3">
            <MapPin size={18} className="text-ink-3 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-[14px] font-semibold text-ink truncate flex items-center gap-1.5">
                {favoriteCenter || t('mobile.profile.pickFavourite')}
                {favoriteCenter && <Star size={13} fill="#c2f02f" style={{ color: '#65a30d' }} />}
              </div>
              <div className="text-[11px] text-ink-3">{t('mobile.profile.centresHint')}</div>
            </div>
            <ChevronRight size={18} className="text-ink-3 flex-shrink-0" />
          </div>
        </Card>
      </div>

      {/* Skills */}
      {Array.isArray(lvl.skills) && lvl.skills.length > 0 && (
        <div className="@wide:col-start-1 @wide:row-start-4">
          <SectionTitle>{t('mobile.profile.levelTraits')}</SectionTitle>
          <Card>
            <ul className="space-y-1.5">
              {lvl.skills.slice(0, 4).map((s: string, i: number) => (
                <li key={i} className="flex items-start gap-2 text-[12px] text-ink-2">
                  <Medal size={13} className="flex-shrink-0 mt-0.5" style={{ color: lvl.color }} />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      )}

      {/* THE RIGHT COLUMN UNDER «PADELSENTRE»: push (native shell only), then
          the account — ONE placed box on a wide screen (#856).

          The push section used to be placed on its own, BELOW the account,
          while coming before it in the markup — and keyboard focus follows the
          markup, so «Slå på varslinger» took focus before «Logg ut» while
          sitting under it. Now the two are stacked in markup order inside one
          box that takes the account's old place, so the focus order is the
          visual order. One box rather than two placements, so the push card's
          height never stretches a row of the LEFT column. The phone is
          unchanged: the same two sections, the same order, the same spacing.
          Without push (the browser), the box holds the account alone, exactly
          where it was. */}
      <div className="space-y-4 @wide:space-y-5 @wide:col-start-2 @wide:row-start-2 @wide:row-span-4">
        {push && (
          <div>
            <SectionTitle>{t('mobile.profile.notifications')}</SectionTitle>
            <PushCard status={push.status} error={push.error} onEnable={push.enable} />
          </div>
        )}

        {/* WHO YOU ARE — a switch only where there is something to switch */}
        <div>
          <SectionTitle>{t('mobile.profile.signedInAs')}</SectionTitle>
          {onSwitchClick ? (
            <>
              <BigButton variant="ghost" onClick={onSwitchClick}>
                <span className="inline-flex items-center gap-2">
                  <RefreshCw size={15} /> {t('mobile.profile.switchProfile')}
                </span>
              </BigButton>
              {/* «Demo — real sign-in arrives once user accounts are in place».
                  True on the paths that still pick; not true for somebody who
                  signed in, which is why both this and the button are gone for
                  them rather than merely inert. */}
              <p className="text-[10px] text-ink-3 mt-2 px-1 flex items-center gap-1">
                <Trophy size={11} /> {t('mobile.profile.demoNote')}
              </p>
            </>
          ) : (
            <p className="text-sm font-medium px-1">{member?.name || '—'}</p>
          )}
          {/* WHICH CLUB IS ACTIVE, under «who you are» (#800, owner
              2026-10-16). The same selector as the 📍 in the header — a context
              to change, not a place to leave. */}
          {onChooseClub && (
            <div className="mt-2">
              <BigButton variant="ghost" onClick={onChooseClub}>
                <span className="inline-flex items-center gap-2">
                  <MapPin size={15} /> {clubName ?? t('mobile.clubContext.choose')}
                </span>
              </BigButton>
            </div>
          )}
          {reportOpen && (
            <div className="mt-2">
              <BigButton variant="ghost" onClick={() => setShowReport(true)}>
                <span className="inline-flex items-center gap-2">
                  <Bug size={15} /> {t('mobile.profile.reportBug')}
                </span>
              </BigButton>
              <p className="text-[10px] text-ink-3 mt-1 px-1">
                {t('mobile.profile.reportBugHint')}
              </p>
            </div>
          )}
          {onSignOut && (
            <div className="mt-2">
              <BigButton variant="ghost" onClick={onSignOut}>
                <span className="inline-flex items-center gap-2">
                  <LogOut size={15} /> {t('mobile.profile.signOut')}
                </span>
              </BigButton>
            </div>
          )}
        </div>

        {/* PASSKEYS BELONG TO THE ACCOUNT, so they sit with «who you are»
            and «Logg ut» rather than with the player's numbers — and they are
            in the same box, so a wide screen keeps them in the account's
            column. The section hides itself where the browser has no
            WebAuthn or the session cannot hold one. */}
        {accountPasskeys && <PasskeySection />}
        {/* «INNLOGGINGER» (#1141): sign out everywhere else — the same
            account-on-the-player-surface condition, since a legacy member
            session has no account whose sign-ins to end. */}
        {accountPasskeys && (
          <div className="mt-4">
            <SessionsSection />
          </div>
        )}
        {/* «MIN PROFIL» (#796): the player edits their own identity — clubs
            may not once an account is linked. The same account-on-the-player-
            surface condition as the passkeys: a legacy member session has no
            account behind it. */}
        {accountPasskeys && (
          <div className="mt-4">
            <MyProfileEditor />
          </div>
        )}
        {/* «AVSLUTT KONTOEN» (#1062): the same account-on-the-player-surface
            condition — a legacy member session has no account to close. */}
        {/* «LAST NED MINE DATA» (#1062): the person's own export, beside the
            closure — the same account-on-the-player-surface condition. */}
        {accountPasskeys && (
          <div className="mt-4">
            <DataExportSection />
          </div>
        )}
        {accountPasskeys && (
          <div className="mt-4">
            <CloseAccountSection onClosed={onSignOut} />
          </div>
        )}
        {/* «SLETT KONTOEN MIN OG DATAENE MINE» (#1062): erasure, last — after
            closure, which it includes. The session is gone afterwards, so it
            leaves through the same sign-out path. */}
        {accountPasskeys && (
          <div className="mt-4">
            <EraseAccountSection onErased={onSignOut} />
          </div>
        )}
      </div>
      {/* The player's own door (`POST /member/bug-reports`) on the player
          surface; the portal's preview files through the portal's. Submit-only:
          no archive link, no counts. */}
      {showReport && (
        <BugReportModal
          currentView="mobile:profile"
          currentContext={{ surface: 'member' }}
          onClose={() => setShowReport(false)}
          mode={playerSurfaceActive() ? 'member' : 'staff'}
        />
      )}
    </div>
  );
}

/**
 * Copy per push status; the button only shows when asking makes sense.
 *
 * `t` is OPTIONAL and defaults to the source catalog, for the same reason
 * `nameOf` does (#310): this is an exported pure function, not a component,
 * so it cannot call `useT()` — and callers that have no translator get the
 * Norwegian they got before, from the catalog rather than from a literal.
 */
export function pushStatusText(
  status: PushStatus,
  t: LocaleContextValue['t'] = (key, vars) => translate(DEFAULT_LOCALE, key, vars),
): { text: string; canEnable: boolean } {
  switch (status) {
    case 'granted':
      return { text: t('mobile.push.granted'), canEnable: false };
    case 'denied':
      return { text: t('mobile.push.denied'), canEnable: false };
    case 'unavailable':
      return { text: t('mobile.push.unavailable'), canEnable: false };
    case 'undetermined':
      return { text: t('mobile.push.undetermined'), canEnable: true };
    case 'unsupported':
      return { text: t('mobile.push.unsupported'), canEnable: false };
    default:
      return { text: t('mobile.push.checking'), canEnable: false };
  }
}

function PushCard({
  status,
  error,
  onEnable,
}: {
  status: PushStatus;
  error?: string | null;
  onEnable: () => void;
}) {
  const t = useT();
  const { text, canEnable } = pushStatusText(status, t);
  return (
    <Card>
      <div className="flex items-start gap-3">
        <Bell size={18} className="text-ink-3 mt-0.5 flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-[13px] text-ink-2">{text}</div>
          {error && <div className="text-[11px] text-ink-3 mt-1">{error}</div>}
          {canEnable && (
            <div className="mt-2">
              <BigButton onClick={onEnable}>{t('mobile.push.enable')}</BigButton>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

function Stat({ label, value, sub }: { label: string; value: any; sub?: string }) {
  return (
    <div
      className="rounded-2xl px-3 py-2.5"
      style={{ background: '#fff', border: '1px solid #ecefe8' }}
    >
      <div className="font-mono text-[9px] uppercase tracking-wider text-ink-3">{label}</div>
      <div
        className="font-display font-semibold text-ink leading-tight tabular-nums"
        style={{ fontSize: 20 }}
      >
        {value}
      </div>
      {sub && <div className="text-[10px] text-ink-3">{sub}</div>}
    </div>
  );
}
